import React, { useEffect, useRef, useState } from "react";
import "./priceRangeFilter.css";
import {
  describePriceRange,
  hasPriceRange,
  priceRangeKind,
  quickAmounts,
  rangeFromInputs,
  rupees,
  type PriceRange,
  type PriceRangeKind,
} from "./priceRange";

type Props = {
  value: PriceRange;
  onChange: (next: PriceRange) => void;
  /** Prices in view, for quick amount suggestions. */
  samplePrices: number[];
};

const KINDS: { value: PriceRangeKind; label: string }[] = [
  { value: "under", label: "Up to" },
  { value: "between", label: "Between" },
  { value: "above", label: "Above" },
];

/** Price filter for a shared catalogue: one chip that opens a small inline panel. */
const PriceRangeFilter: React.FC<Props> = ({ value, onChange, samplePrices }) => {
  const active = hasPriceRange(value);
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<PriceRangeKind>(priceRangeKind(value));
  const [minRaw, setMinRaw] = useState(value.min ? String(value.min) : "");
  const [maxRaw, setMaxRaw] = useState(value.max ? String(value.max) : "");
  const [error, setError] = useState("");
  const firstInput = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setKind(priceRangeKind(value));
    setMinRaw(value.min ? String(value.min) : "");
    setMaxRaw(value.max ? String(value.max) : "");
    setError("");
  }, [value.min, value.max]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => firstInput.current?.focus({ preventScroll: true }), 40);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, kind]);

  const apply = (e?: React.FormEvent) => {
    e?.preventDefault();
    const next = rangeFromInputs(kind, minRaw, maxRaw);
    if (typeof next === "string") {
      setError(next);
      return;
    }
    setError("");
    setOpen(false);
    onChange(next);
  };

  const pickQuick = (amount: number) => {
    if (kind === "under") {
      setMaxRaw(String(amount));
      onChange({ max: amount });
      setOpen(false);
    } else if (kind === "above") {
      setMinRaw(String(amount));
      onChange({ min: amount });
      setOpen(false);
    } else if (!minRaw) {
      setMinRaw(String(amount));
    } else {
      setMaxRaw(String(amount));
    }
  };

  const quick = quickAmounts(samplePrices);
  const input = (id: string, label: string, val: string, set: (v: string) => void, ref?: React.Ref<HTMLInputElement>) => (
    <div className="hkp-price-input">
      <label className="hkp-price-input-label" htmlFor={id}>
        {label}
      </label>
      <span className="hkp-price-input-box">
        <span aria-hidden="true">₹</span>
        <input
          ref={ref}
          id={id}
          inputMode="numeric"
          autoComplete="off"
          enterKeyHint="done"
          value={val}
          onChange={(e) => set(e.target.value.replace(/[^\d,]/g, ""))}
        />
      </span>
    </div>
  );

  return (
    <div className="hkp-price-filter" ref={rootRef}>
      <div className="hkp-price-filter-row">
        <button
          type="button"
          className={`hkp-price-chip${active ? " is-active" : ""}`}
          aria-expanded={open}
          aria-controls="hkp-price-panel"
          onClick={() => setOpen((o) => !o)}
        >
          <span>{active ? describePriceRange(value) : "Price"}</span>
          <span className="hkp-price-chip-caret" aria-hidden="true">
            ▾
          </span>
        </button>
        {active ? (
          <button
            type="button"
            className="hkp-price-clear"
            onClick={() => {
              setOpen(false);
              onChange({});
            }}
          >
            Clear
          </button>
        ) : null}
      </div>
      {open ? (
        <form id="hkp-price-panel" className="hkp-price-panel" onSubmit={apply} noValidate>
          <div className="hkp-price-kinds" role="radiogroup" aria-label="Price range type">
            {KINDS.map((k) => (
              <button
                key={k.value}
                type="button"
                role="radio"
                aria-checked={kind === k.value}
                className={kind === k.value ? "is-active" : ""}
                onClick={() => {
                  setKind(k.value);
                  setError("");
                }}
              >
                {k.label}
              </button>
            ))}
          </div>
          <div className="hkp-price-inputs">
            {kind !== "under" ? input("hkp-price-min", kind === "between" ? "From" : "Lowest", minRaw, setMinRaw, firstInput) : null}
            {kind !== "above"
              ? input("hkp-price-max", kind === "between" ? "To" : "Highest", maxRaw, setMaxRaw, kind === "under" ? firstInput : undefined)
              : null}
          </div>
          <div className="hkp-price-quick" aria-label="Quick amounts">
            {quick.map((a) => (
              <button key={a} type="button" className="hkp-price-quick-chip" onClick={() => pickQuick(a)}>
                {rupees(a)}
              </button>
            ))}
          </div>
          {error ? (
            <p className="hkp-price-error" role="alert">
              {error}
            </p>
          ) : null}
          <div className="hkp-price-actions">
            <button type="button" className="hkp-price-btn is-quiet" onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button type="submit" className="hkp-price-btn">
              Show pieces
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
};

export default PriceRangeFilter;
